/**
 * Utility functions for CSV export
 */

export interface CSVColumn<T = any> {
  /** Column header text */
  header: string;
  /** Function to extract and format value from row */
  getValue: (row: T) => string | number | null | undefined;
  /** Optional condition - column only included if true (default: true) */
  condition?: boolean;
}

export interface ExportToCSVOptions<T = any> {
  /** Data rows to export */
  data: T[];
  /** Column definitions */
  columns: CSVColumn<T>[];
  /** Output filename (without extension) */
  filename: string;
}

/**
 * Escape CSV value to handle commas, quotes, and newlines
 */
const escapeCSVValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Export data to CSV file and trigger download
 *
 * @example
 * exportToCSV({
 *   data: users,
 *   columns: [
 *     { header: 'Name', getValue: (row) => row.name },
 *     { header: 'Email', getValue: (row) => row.email },
 *     { header: 'Status', getValue: (row) => row.isActive ? 'Active' : 'Inactive' },
 *     { header: 'Revenue', getValue: (row) => row.revenue.toFixed(2) },
 *   ],
 *   filename: 'users-export'
 * });
 *
 * @example
 * // With conditional columns
 * exportToCSV({
 *   data: campaigns,
 *   columns: [
 *     { header: 'Campaign', getValue: (row) => row.name },
 *     { header: 'Date', getValue: (row) => row.date, condition: showDates },
 *     { header: 'Spend', getValue: (row) => row.spend.toFixed(2) },
 *   ],
 *   filename: 'campaign-report'
 * });
 */
export const exportToCSV = <T = any>({ data, columns, filename }: ExportToCSVOptions<T>): void => {
  // Filter columns based on condition
  const activeColumns = columns.filter(col => col.condition !== false);

  // Create headers
  const headers = activeColumns.map(col => escapeCSVValue(col.header));

  // Create data rows
  const rows = data.map(row => {
    return activeColumns.map(col => {
      const value = col.getValue(row);
      return escapeCSVValue(value);
    });
  });

  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.join(','))
    .join('\n');

  // Create blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up URL object
  URL.revokeObjectURL(url);
};
